export const productUpdates = [
{
  "title": "New in Rill: record the tab you already use",
  "date": "2026-11-02",
  "slug": "record-the-tab-you-already-use",
  "description": "Record signed-in websites and localhost with Rill for Chrome, get your coding agent connected, and try our November Pro offer.",
  "intro": "That bug you can only reproduce when you’re signed in? Now you can record it right where it happens, with Rill for Chrome. This week, we’re also making it easier to get your coding agent ready to record—and giving you a November offer to try Pro.",
  "sections": [
    {
      "heading": "Record right where the problem happens",
      "paragraphs": [
        "Last week, we shared a peek at recording a tab in your own Chrome browser. It’s now ready to try.",
        "Add Rill to Chrome, choose the website tab you want to record, and show what happened. It works with signed-in websites and apps on localhost, too. Your recording saves privately in Rill, ready to watch or share.",
        "Try it with one thing you’ve been meaning to explain: a button that behaves strangely, a change you want feedback on, or a flow that’s easier to show than describe.",
        "You can start directly from the extension, or connect it through Rill in ChatGPT.",
        "[Try Rill for Chrome →](https://userill.dev/chrome)"
      ],
      "image": {
        "src": "/blog-assets/2026-11-02/chrome.png",
        "alt": "Illustration of a Chrome tab being recorded and saved to Rill."
      }
    },
    {
      "heading": "Get your coding agent ready to record",
      "paragraphs": [
        "Which coding agent are you using—Codex, Cursor, or Claude Code? Record what it checks in the browser, so you can watch the steps back and share what happened.",
        "Choose “Use your coding agent” in Rill and follow the setup steps. You can see when it connects and open its new recording from the same screen.",
        "Then ask your browser-capable agent to record one small change you’ve made this week. Your agent runs the browser steps; Rill gives you a recording to review afterward.",
        "[Get your agent ready →](https://userill.dev/connect/agent)"
      ],
      "image": {
        "src": "/blog-assets/2026-11-02/agent.png",
        "alt": "Illustration of choosing a coding agent, connecting it, and opening a recording."
      }
    },
    {
      "heading": "Your next walkthrough, on Pro",
      "paragraphs": [
        "Have a change to walk a client through, or a bug your teammate needs to see? Record it once and send them the link. This November, try Rill Pro for **$2/month for your first two months**.",
        "[Get two months of Pro for $2/month →](https://userill.dev/adminbar?utm_source=rill_blog&utm_medium=owned_content&utm_campaign=black_friday_2026&utm_content=weekly_update_2026_11_02)",
        "How are you using Rill, and what would you like us to build next? [Tell us](mailto:hello@team.userill.dev). We’d love to hear from you, and we read every message.",
        "With love from the Rill Team 💜"
      ]
    }
  ]
},
{
  "title": "New in Rill: put yourself in the picture",
  "date": "2026-10-26",
  "slug": "put-yourself-in-the-picture",
  "description": "Add a camera circle to your walkthrough, keep recording controls nearby, and open shared recordings in your Rill workspace.",
  "intro": "Pick one change you made this week and show someone how it works. You can now add your face to a Rill screen recording, making a walkthrough feel a little more personal.\n",
  "sections": [
    {
      "heading": "Your screen, with you in the picture",
      "paragraphs": [
        "Show the change, explain the part that matters, and let your teammate see you alongside it. Rill’s screen recorder now supports a camera circle, with optional narration.",
        "Try it with something small: a new page, a bug you’ve reproduced, or a flow you want feedback on. A short walkthrough gives someone a place to start the conversation.",
        "[Record my walkthrough →](https://userill.dev/record)"
      ],
      "image": {
        "src": "/blog-assets/2026-10-26/camera.png",
        "alt": "Illustration of a screen recording with a camera circle."
      }
    },
    {
      "heading": "Keep the controls nearby",
      "paragraphs": [
        "In Chrome and Edge, you can pop out the recorder to keep its controls close while you move through your walkthrough."
      ]
    },
    {
      "heading": "Shared recordings feel more at home",
      "paragraphs": [
        "When you’re signed in, a teammate’s recording now opens inside your Rill workspace, alongside the familiar tools you use to review your own work.",
        "The next time someone sends you a Rill, open it while signed in and take a look."
      ],
      "image": {
        "src": "/blog-assets/2026-10-26/shared.png",
        "alt": "Illustration of a shared recording inside the Rill workspace."
      }
    },
    {
      "heading": "One more thing: record your local app",
      "paragraphs": [
        "Today, you can already use Rill’s CLI with a browser-capable coding agent to record an app on localhost. Watch and share the replay when the recording is ready.",
        "It’s a useful way to show a change before your app is public: your agent runs the browser flow, and Rill records what happened.",
        "[Record with my coding agent →](https://userill.dev/docs/agent-quickstart)",
        "A peek at what’s next",
        "We're now testing a new way to record a tab in your own Chrome browser and bring the video into ChatGPT.",
        "For now, pick one change worth showing and [make a walkthrough](https://userill.dev/record)."
      ]
    }
  ]
},
{
  "title": "New in Rill: meet Pebble, your recording review buddy",
  "date": "2026-10-19",
  "slug": "meet-pebble-your-recording-review-buddy",
  "description": "Get a second pair of eyes with Pebble, follow browser recordings more easily, and bring them into Notion. Plus, more room on Free.",
  "intro": "Got something you’ve been meaning to take another look at? Bring it to Rill. This week, Pebble joins you for the review, and we’ve made it easier to follow what happened and share it with your team.",
  "sections": [
    {
      "heading": "Meet Pebble",
      "paragraphs": [
        "Want a second pair of eyes on a recording? Open it and choose Ask Pebble. Pebble reviews moments from the video and the activity captured alongside it, then leaves notes about possible bugs and confusing parts of the experience.",
        "Pick a note, watch the moment it refers to, and see what you think. You can reply with a thought or dismiss a suggestion that doesn’t fit.",
        "[Meet Pebble in your recordings →](https://userill.dev/)"
      ]
    },
    {
      "heading": "Follow the action, find the moment",
      "paragraphs": [
        "Ever watched a recording and wondered, “What happened just before that?” It’s now easier to follow each step, spot a click, and find the moment you want to look at again.",
        "Try it with a page you’ve changed recently. Let Rill check it, then watch the journey back.",
        "[Give your latest change a try →](https://userill.dev/browser-check)"
      ]
    },
    {
      "heading": "Put the recording where the conversation is",
      "paragraphs": [
        "A quick walkthrough can say more than a long explanation. You can now watch Rill recordings right inside Notion, or add them to your website.",
        "Keep the recording beside your notes so your team can see what you mean, right where you’re already talking about it.",
        "[Pick a recording to share →](https://userill.dev/)"
      ]
    },
    {
      "heading": "Smoother recording on Windows, Mac, and Linux",
      "paragraphs": [
        "Building with a coding agent on Windows? You can now record your work with Rill there too, alongside Mac and Linux.",
        "Bring your latest change into Rill and see how it looks in action. We’ve also made saving recordings smoother, so it’s easier to get back to reviewing what you built.",
        "[Try recording with your agent →](https://userill.dev/docs/agent-quickstart)"
      ]
    },
    {
      "heading": "One more thing",
      "paragraphs": [
        "More room on Free.",
        "Free now includes 10 recordings a day, 200 minutes of saved video, and 10 browser tasks each month. There’s more room to try an idea, check a change, and share what happened.",
        "[See what’s included →](https://userill.dev/pricing)",
        "Come say hello to Pebble—and if there’s something you’d love a little help spotting, tell us at hello@runalabs.dev. We’d love to hear it.",
        "With love from the Rill Team 💜"
      ]
    }
  ]
},
{
  "title": "New in Rill: say it, run it, watch it back",
  "date": "2026-10-12",
  "slug": "say-it-run-it-watch-it-back",
  "description": "Describe browser checks with your voice, turn a website into a video, and bring your recordings into ChatGPT.",
  "intro": "Some things are easier to explain out loud. This week, Rill gives you a new way to start a browser check: say what you want to try, then watch what happened. There’s also a quick way to turn your website into a video, a walkthrough flow for Freebuff, and a connection that brings your recordings into ChatGPT.\n",
  "sections": [
    {
      "heading": "Talk it through with Rill",
      "paragraphs": [
        "When the steps are clear in your head but hard to put into a short prompt, use your voice. Enter your website URL, click the microphone, and describe the task.",
        "For example: “Open the pricing page, choose the monthly plan, and stop before paying.”",
        "Rill turns your instructions into a browser task and records what happens. Watch the result to see where the flow went smoothly and where it needs another look.",
        "[Try voice input](https://userill.dev/browser-check)."
      ]
    },
    {
      "heading": "Turn your website into a video",
      "paragraphs": [
        "Need a quick preview of your landing page for a launch post or a message to a teammate? Paste a public URL into Website to Video. Rill creates a scrolling video you can download or share.",
        "No signup needed, no watermark.",
        "[Make a website video](https://userill.dev/tools/website-to-video)."
      ]
    },
    {
      "heading": "Bring a Freebuff walkthrough to life",
      "paragraphs": [
        "After building something in Freebuff, ask it to prepare a walkthrough. Open its link in Rill and add your public app URL.",
        "Review the steps, sign in, and start the run. Rill follows the walkthrough in a browser while recording the result, giving you something concrete to review and share.",
        "[Try the Freebuff walkthrough](https://userill.dev/freebuff)."
      ]
    },
    {
      "heading": "Review your recordings with ChatGPT",
      "paragraphs": [
        "Connect Rill to ChatGPT and start with: “Rill, open my latest recording.”",
        "You can review a browser check, look closer at a moment, or turn what happened into a bug report. It’s another way to work through the recording when you want help deciding what to investigate next.",
        "[Connect Rill to ChatGPT](https://chatgpt.com/plugins/plugin_asdk_app_6ac4f28bdf8c81918d5932fe22e7d8e2)."
      ]
    },
    {
      "heading": "Help shape what comes next",
      "paragraphs": [
        "We’re building an iOS app and a companion Mac app. They’re still in development, and we’d love your feedback as we shape them.",
        "[Join the beta list](https://forms.gle/zBBygq1dj56jKFPJA).",
        "With love from the Rill Team 💜"
      ]
    }
  ]
},
  {
  "title": "New in Rill: run it again, see who watched",
  "date": "2026-10-05",
  "slug": "run-it-again-see-who-watched",
  "description": "Repeat cloud runs, see human views and agent activity, share diagnostic moments, and collect screen recordings.",
  "intro": "This week’s updates make it easier to recheck a website after a change, see how your recordings are used, and bring someone else straight to the moment that matters.",
  "sections": [
    {
      "heading": "Updated your website? Run it again.",
      "paragraphs": [
        "The retry button we mentioned last week is here. Click Run again in your recording library to repeat a cloud run with the same settings—no need to enter the URL and task again.",
        "It’s a quick way to recheck a flow after a change. Each repeat uses one cloud-run credit."
      ]
    },
    {
      "heading": "See how your recordings are being used",
      "paragraphs": [
        "Your recordings now have Viewers and Insights, with human playback and agent access shown separately. See view counts, activity over time, and how much of a recording people watched.",
        "These insights are visible only to you, and your own playback doesn’t inflate the count."
      ]
    },
    {
      "heading": "Share the moment that matters",
      "paragraphs": [
        "Click a timestamp in the console, network, or timeline to jump to that point in the recording. Copy its share link so the next person opens at the same moment, with the relevant diagnostic event selected."
      ]
    },
    {
      "heading": "Record your screen",
      "paragraphs": [
        "Capture a desktop screen, window or tab with an optional microphone, then preview, download or upload it. Use it to show a bug, explain a change, or walk someone through a flow.",
        "Screen recordings capture what you show on screen; they do not automatically include browser console or network diagnostics."
      ]
    },
    {
      "heading": "Request a recording",
      "paragraphs": [
        "Need to see a problem on someone else’s screen? Send a recording request link so they can record without an account and submit it straight to your library.",
        "[Read the guide to requesting a customer recording →](https://userill.dev/blog/ask-customer-to-record-a-bug-without-a-support-call)"
      ]
    },
    {
      "heading": "A peek at what’s next",
      "paragraphs": [
        "Rill for iPhone and Mac. You’ve been asking for an iOS app to record on the go and a companion Mac app. We’re building them. To join the early-tester list, email hello@runalabs.dev with “early tester”. Get one month of Rill Pro free when you take part. Your feedback will help shape both apps.",
        "Talk it through with Rill. Prefer speaking to typing, or have too many thoughts to type them all out? We’re working on voice input—even if it comes out a little messy, Rill will turn it into a clear browser test.",
        "These are upcoming features, not part of this week’s release."
      ]
    },
    {
      "heading": "Try the updates",
      "paragraphs": [
        "[Sign in to your recordings →](https://userill.dev/login)",
        "What would you like to record next? Tell us at hello@runalabs.dev. We read everything 🤩"
      ]
    }
  ]
},
  {
    "title": "New in Rill: 3× playback and easier agent setup",
    "date": "2026-09-28",
    "slug": "faster-playback-and-easier-agent-setup",
    "description": "Review recordings at up to 3× speed, start a local agent recording before signing in, and keep your agent connected for up to 90 days.",
    "intro": "This week’s updates help you review recordings faster and get your coding agent recording sooner. Playback speed controls are now in the player, and your agent can start a local browser recording before you sign in.",
    "sections": [
      {
        "heading": "Review recordings at up to 3× speed",
        "paragraphs": [
          "Change playback speed directly in the player, with options up to 3×. There’s no need to open Settings when you want to move through a recording faster.",
          "Rill remembers your preferred speed in that browser, whether you’re watching your own recording or a shared one."
        ]
      },
      {
        "heading": "Record first. Connect your account afterward.",
        "paragraphs": [
          "Your coding agent can now start a local browser recording before you sign in. When you’re ready to keep it, connect the agent to your Rill account and claim the recording.",
          "The share link stays the same, so you can continue using the link you already have.",
          "The new [agent quickstart](https://userill.dev/docs/agent-quickstart) includes one prompt to copy into your coding agent to get started."
        ]
      },
      {
        "heading": "Connect once, keep recording",
        "paragraphs": [
          "Approve your agent’s connection in the browser for up to 90 days. Automatic token renewal keeps the connection active during that period, so you can keep recording without repeatedly connecting your account.",
          "You can revoke the connection from Credentials whenever you need to.",
          "[Set up your agent →](https://userill.dev/docs/agent-quickstart)"
        ]
      }
    ]
  },
  {
    "title": "New in Rill: Cloud runs and visible clicks",
    "date": "2026-09-21",
    "slug": "cloud-runs-and-visible-clicks",
    "description": "Start browser walkthroughs from your dashboard, follow the agent’s clicks, and record localhost without setting up FFmpeg.",
    "intro": "This week’s updates make it easier to start a browser walkthrough and follow what happened in the recording. You can give Rill a URL and a task from your dashboard, see the agent’s cursor and clicks, and record localhost without setting up FFmpeg.",
    "sections": [
      {
        "heading": "Give Rill a URL and a task",
        "paragraphs": [
          "You can now start a browser walkthrough directly from your dashboard. Enter a URL, describe what you want the agent to do, and Rill runs it in the cloud.",
          "For example:",
          "> Open the pricing page and compare the available plans.",
          "Review the recording afterward to see which pages the agent visited and what it did. The recording gives you something to inspect when deciding whether the task was completed."
        ]
      },
      {
        "heading": "See where the agent clicks",
        "paragraphs": [
          "Recordings now show the mouse cursor and highlight clicks, making it easier to follow the agent as it moves through a page.",
          "Click highlighting is on by default. You can change its color in Settings."
        ]
      },
      {
        "heading": "Record localhost without setting up FFmpeg",
        "paragraphs": [
          "When you record a site running on localhost, the browser stays on your machine and Rill handles video processing in the cloud. You no longer need a local FFmpeg setup.",
          "Update to the latest Rill CLI to use this workflow.",
          "[Start a cloud run →](https://userill.dev/)"
        ]
      }
    ]
  }
] as const;
